import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AgentAuditOperationDetail from '../Components/AgentAuditOperationDetail';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import TestAgentModal from '../Components/TestAgentModal';

import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { PromptApi } from '../Services/PromptApiProviderTypes';
import { AuditOperation } from '../Services/TaskScheduleApiProviderTypes';

function AgentAuditOperationContent() {
  const navigate = useNavigate();
  const { organizationId, agentId, operationId } = useParams();
  const [searchParams] = useSearchParams();
  const runKey = operationId || searchParams.get('runKey');

  const { getAgentById, getAgentAuditOperationById, getPrompts } = useAgentApi();

  const getAgentAuditOperationByIdRef = useRef(getAgentAuditOperationById);
  getAgentAuditOperationByIdRef.current = getAgentAuditOperationById;

  const [agentLoading, setAgentLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState<string>('');
  const [promptId, setPromptId] = useState<string>('');
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [isPromptRealtime, setIsPromptRealtime] = useState<boolean>(false);

  const [detailAudit, setDetailAudit] = useState<AuditOperation | null>(null);

  // ── Prompt realtime detection ─────────────────────────────────────────
  useEffect(() => {
    if (!promptId || !prompts?.length) {
      setIsPromptRealtime(false);
      return;
    }
    const selectedPrompt = prompts.find((p) => p.id === promptId);
    setIsPromptRealtime(selectedPrompt?.pattern_type === 'realtime');
  }, [promptId, prompts]);

  // ── Fetch agent info ──────────────────────────────────────────────────
  useEffect(() => {
    if (agentId === 'new-agent') return;

    const f = async () => {
      setAgentLoading(true);
      try {
        const [agent, promptsList] = await Promise.all([getAgentById(agentId), getPrompts()]);
        setName(agent.name);
        setPromptId(agent.prompt_id);
        setPrompts(promptsList);
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      } finally {
        setAgentLoading(false);
      }
    };

    f();
  }, [agentId]);

  // ── Fetch audit detail by runKey ──────────────────────────────────────
  useEffect(() => {
    if (!runKey || agentId === 'new-agent') return;

    const f = async () => {
      setDetailLoading(true);
      try {
        const detail = await getAgentAuditOperationByIdRef.current(agentId, runKey);
        if (detail) {
          setDetailAudit(detail);
        } else {
          setError('Audit operation not found');
        }
      } catch (err) {
        console.error('Error fetching audit detail:', err);
        setError('Failed to load audit operation detail');
      } finally {
        setDetailLoading(false);
      }
    };

    f();
  }, [agentId, runKey]);

  const basePath = `/organizations/${organizationId}/agents-hub/${agentId}`;

  const handleBack = (): void => {
    navigate(`${basePath}/audit-operations`);
  };

  const loading = agentLoading || detailLoading;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title m-0">{name}</h2>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="small" onClick={handleBack}>
                Back
              </Button>

              <Button variant="secondary" size="large" onClick={() => navigate(basePath)}>
                View Settings
              </Button>

              <Button
                variant="secondary"
                size="large"
                onClick={() => navigate(`${basePath}/instructions`)}
              >
                View Instructions
              </Button>
            </div>
          </div>

          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              {loading ? (
                <PageLoader />
              ) : error ? (
                <div>Error: {error}</div>
              ) : detailAudit ? (
                <AgentAuditOperationDetail
                  agentAudit={detailAudit}
                  onClose={handleBack}
                  showId={true}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="radius-yes p-2 bg-yes radius col-6">
          <h2 className="h2-title">Test Agent</h2>
          <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
            <TestAgentModal isOpen={true} isPromptRealtime={isPromptRealtime} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentAuditOperationPage() {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <AgentAuditOperationContent />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentAuditOperationPage;
